/**
 * Utility for handling Firebase authentication errors
 * Maps Firebase error codes to user-friendly messages
 */

/**
 * Get a user-friendly error message from a Firebase auth error code
 * @param {string} errorCode - The Firebase error code
 * @param {string} [defaultMessage="An unexpected error occurred. Please try again."] - Default message if no specific message is found
 * @returns {string} User-friendly error message
 */
export const getAuthErrorMessage = (errorCode, defaultMessage = "An unexpected error occurred. Please try again.") => {
  // Map of Firebase error codes to user-friendly messages
  const errorMessages = {
    // Email/Password Authentication Errors
    'auth/email-already-in-use': 'This email address is already in use. Please try signing in or use a different email.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/user-disabled': 'This account has been disabled. Please contact support for assistance.',
    'auth/user-not-found': 'No account found with this email address. Please check your email or create a new account.',
    'auth/wrong-password': 'Incorrect password. Please try again or reset your password.',
    'auth/too-many-requests': 'Too many unsuccessful login attempts. Please try again later or reset your password.',
    'auth/weak-password': 'Password is too weak. Please use at least 6 characters with a mix of letters, numbers, and symbols.',

    // Account Creation Errors
    'auth/operation-not-allowed': 'This operation is not allowed. Please contact support.',
    'auth/invalid-credential': 'The provided credential is invalid. Please try again with valid information.',
    'auth/invalid-verification-code': 'The verification code you entered is invalid. Please try again.',
    'auth/invalid-verification-id': 'The verification ID is invalid. Please request a new verification code.',
    'auth/missing-verification-code': 'Please enter the verification code sent to your device.',
    'auth/missing-verification-id': 'Verification information is missing. Please restart the verification process.',

    // Network Errors
    'auth/network-request-failed': 'A network error occurred. Please check your internet connection and try again.',

    // Timeout Errors
    'auth/timeout': 'The operation has timed out. Please try again.',

    // Misc Errors
    'auth/cancelled-popup-request': 'The authentication request was cancelled. Please try again.',
    'auth/popup-blocked': 'The authentication popup was blocked by your browser. Please allow popups for this site and try again.',
    'auth/popup-closed-by-user': 'The authentication popup was closed before completing the sign-in process. Please try again.',
    'auth/unauthorized-domain': 'This domain is not authorized for OAuth operations. Please contact support.',
    'auth/invalid-action-code': 'The action code is invalid. This can happen if the code is malformed, expired, or has already been used.',
    'auth/expired-action-code': 'The action code has expired. Please request a new one.',
    'auth/quota-exceeded': 'Operation quota has been exceeded. Please try again later.',

    // Google Sign-In Errors
    'auth/account-exists-with-different-credential': 'An account already exists with this email using a different sign-in method. Please sign in with your original method.',
    'auth/credential-already-in-use': 'This credential is already associated with another account.',
    'auth/requires-recent-login': 'Please sign in again to complete this action.',

    // Email Verification Errors
    'auth/email-not-verified': 'Please verify your email address to access all features.',
  };

  // Return the specific error message if it exists, otherwise return the default message
  return errorMessages[errorCode] || defaultMessage;
};

/**
 * Log authentication errors for debugging purposes
 * @param {Error} error - The error object
 * @param {string} context - The context where the error occurred (e.g., 'login', 'register')
 */
export const logAuthError = (error, context) => {
  console.error(`Auth Error (${context}):`, {
    code: error.code,
    message: error.message,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Analyze password strength and return feedback
 * @param {string} password - The password to analyze
 * @returns {Object} Feedback object with score and message
 */
export const analyzePasswordStrength = (password) => {
  // Basic password strength analysis
  const hasLowerCase = /[a-z]/.test(password);
  const hasUpperCase = /[A-Z]/.test(password);
  const hasNumbers = /[0-9]/.test(password);
  const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const isLongEnough = password.length >= 8;

  // Calculate score (0-4)
  let score = 0;
  if (hasLowerCase) score++;
  if (hasUpperCase) score++;
  if (hasNumbers) score++;
  if (hasSpecialChars) score++;
  if (isLongEnough) score++;

  // Normalize score to 0-4 range
  score = Math.min(4, Math.floor(score * 4 / 5));

  // Generate feedback message
  let message = '';
  switch (score) {
    case 0:
      message = 'Very weak password. Please use a stronger password.';
      break;
    case 1:
      message = 'Weak password. Try adding numbers, symbols, or uppercase letters.';
      break;
    case 2:
      message = 'Fair password. Consider making it longer or more complex.';
      break;
    case 3:
      message = 'Good password. For better security, try adding more variety.';
      break;
    case 4:
      message = 'Strong password!';
      break;
    default:
      message = 'Please enter a password.';
  }

  return { score, message };
};